// ============================================================================
// LE CLIENT D'UNE FACTURE, FIGÉ SUR LA PIÈCE — ce qui est signé, ce qui est gardé à côté, et ce qui s'imprime.
//
// UTILITÉ : une facture porte l'identité de son client (R12, feuillets « Client professionnel » et « Client
// particulier ») ; cette identité est arrêtée UNE fois, à l'émission, puis relue telle quelle par l'impression, le
// duplicata, la facture d'avoir qui la corrige et l'archive fiscale.
// ⚠️ JAMAIS LA FICHE DU JOUR APRÈS L'ÉMISSION : une adresse ou une raison sociale changée depuis ferait réimprimer un
//    autre document que celui qui a été remis (§6.3.1.1), et la facture d'avoir porterait un client que la facture ne
//    connaît pas.
// ⚠️ DEUX MOITIÉS, et la frontière est celle du référentiel : les trois zones que R12 met dans la SIGNATURE
//    (`TAG-FAC-CLI-NOM`/`-CPP`/`-TVA`) partent dans la charge signée ; le reste (adresse, ville, pays, SIRET, la
//    personne) est figé dans des colonnes hors signature, inaltérables, que l'archive remet à l'auditeur.
// ⚠️ DANS LE MOTEUR DE RENDU, PAS DANS LA CAISSE : la fiche client du back-office montre « Sur la facture » ce que la
//    caisse imprimera, et cet aperçu doit LIRE la règle d'impression — redessinée à côté, il annoncerait une facture qui
//    n'existe pas (le NAF qu'elle n'imprime pas, la personne qu'elle imprime).
// ============================================================================
import type { ClientIdentity } from '@orgazone/core-nf525';
import type { FactureClientBlock } from './builders';

/** Le bloc client d'une facture gardé hors de la charge signée — vide sur un ticket et sur une facture d'avant. */
export interface ClientBlock {
  /** La personne, quand le nom signé est une raison sociale — vide sinon. */
  contactName: string;
  address: string;
  city: string;
  country: string;
  siret: string;
}

export const EMPTY_CLIENT_BLOCK: ClientBlock = Object.freeze({ contactName: '', address: '', city: '', country: '', siret: '' });

/** Le client tel qu'une pièce le fige : les zones signées et le bloc qui les accompagne. */
export interface FrozenClient {
  identity: ClientIdentity;
  block: ClientBlock;
}

/** Ce que la fiche client fournit à l'émission d'une facture. */
export interface InvoiceClientCard {
  lastName: string;
  firstName: string | null;
  isPro: boolean;
  companyLegalName: string | null;
  address1: string | null;
  address2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  siret: string | null;
  vatNumber: string | null;
}

const clean = (v: string | null | undefined): string => (v ?? '').trim();

/**
 * Le client d'une facture, arrêté depuis sa fiche au moment de l'émettre (décisions utilisateur).
 * ⚠️ UN CLIENT PRO EST FACTURÉ À SA RAISON SOCIALE : c'est elle qui est signée (R12 « Nom ou raison sociale »), la
 *    personne reste imprimée dessous. Sans raison sociale saisie, le nom de la personne — on ne fabrique rien.
 * ⚠️ LE N° DE TVA ET LE SIRET NE VALENT QUE POUR UN PRO : une fiche particulier qui en porterait par erreur ne les
 *    signe pas sur une facture de particulier.
 */
export function invoiceClientOf(card: InvoiceClientCard): FrozenClient {
  const person = card.firstName ? `${clean(card.lastName)} ${clean(card.firstName)}` : clean(card.lastName);
  const company = card.isPro ? clean(card.companyLegalName) : '';
  return {
    identity: {
      nom: company || person,
      codePostal: clean(card.postalCode),
      tvaIntra: card.isPro ? clean(card.vatNumber) : '',
    },
    block: {
      contactName: company ? person : '',
      address: [clean(card.address1), clean(card.address2)].filter(Boolean).join(', '),
      city: clean(card.city),
      country: clean(card.country),
      siret: card.isPro ? clean(card.siret) : '',
    },
  };
}

/**
 * Le bloc « Facturé à » imprimé, lu sur ce que la pièce a figé.
 * ⚠️ UNE FACTURE D'AVANT LE BLOC FIGÉ s'imprime à son seul nom signé : c'est tout ce qu'elle a gardé, et relire la fiche
 *    du jour réimprimerait un autre client que celui de l'original (M5).
 */
export function printedClientBlock(frozen: FrozenClient): FactureClientBlock {
  const { identity: id, block } = frozen;
  const zipCity = [id.codePostal, block.city].filter(Boolean).join(' ');
  const address = [block.address, zipCity, block.country].filter(Boolean);
  return {
    name: block.contactName || id.nom || 'N/C',
    ...(block.contactName && id.nom ? { companyName: id.nom } : {}),
    ...(address.length ? { address } : {}),
    ...(block.siret ? { siret: block.siret } : {}),
    ...(id.tvaIntra ? { tvaNum: id.tvaIntra } : {}),
  };
}
